import React from 'react';
import useIsMobile from '../utils/useIsMobile';

// Skeleton component for loading states
export function Skeleton({ 
  className = '', 
  width, 
  height, 
  borderRadius = 'var(--radius)',
  style = {} 
}) {
  return (
    <div 
      className={`skeleton ${className}`}
      style={{
        width,
        height,
        borderRadius,
        ...style
      }}
    />
  );
}

// Pre-built skeleton components for common UI elements
export function SkeletonText({ lines = 1, width = '100%', className = '' }) {
  return (
    <div className={`skeleton-text-group ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          height="1em"
          width={i === lines - 1 && lines > 1 ? '70%' : width}
          style={{ marginBottom: i < lines - 1 ? '0.5em' : 0 }}
        />
      ))}
    </div>
  );
}

export function SkeletonImage({ aspectRatio = '1/1', className = '' }) {
  return (
    <div className={`skeleton-image ${className}`} style={{ aspectRatio }}>
      <Skeleton width="100%" height="100%" />
    </div>
  );
}

export function SkeletonCard({ className = '' }) {
  return (
    <div className={`skeleton-card ${className}`}>
      <SkeletonImage />
      <div className="skeleton-card-content">
        <SkeletonText lines={2} />
      </div>
    </div>
  );
}

export function SkeletonPhotoGrid({ count = 36, tileSize = 200, className = '' }) {
  const isMobile = useIsMobile();
  const minSize = isMobile ? '45%' : `${tileSize}px`;

  return (
    <div
      className={`grid gap-3 md:gap-4 pt-3 md:pt-4 ${className}`}
      style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${minSize}, 1fr))` }}
    >
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="relative overflow-hidden rounded-md aspect-square">
          <Skeleton width="100%" height="100%" />
        </div>
      ))}
    </div>
  );
}

export function SkeletonAlbumGrid({ count = 6 }) {
  return (
    <div className="albums-grid">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="album-card skeleton-album">
          <div className="album-card-cover">
            <Skeleton width="100%" height="100%" />
          </div>
          <div className="album-card-content">
            <SkeletonText lines={1} width="60%" />
            <SkeletonText lines={1} width="40%" />
          </div>
        </div>
      ))}
    </div>
  );
}

// Mobile-specific skeleton components
export function SkeletonMobileHeader() {
  return (
    <div className="mobile-header skeleton-mobile-header">
      <div className="mobile-header-content">
        <Skeleton width="32px" height="32px" borderRadius="50%" />
        <div style={{ flex: 1, marginLeft: '12px' }}>
          <SkeletonText lines={1} width="50%" />
          <SkeletonText lines={1} width="30%" />
        </div>
      </div>
    </div>
  );
}

export function SkeletonMobilePhotoGrid({ count = 9 }) {
  const isMobile = useIsMobile();
  const gridClass = isMobile ? 'photo-grid mobile-grid' : 'photo-grid';
  
  return (
    <div className={gridClass}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="photo-tile skeleton-photo-tile">
          <Skeleton width="100%" height="100%" />
        </div>
      ))}
    </div>
  );
}

export function SkeletonMobileAlbum() {
  return (
    <div className="mobile-album-skeleton">
      <div className="album-header-skeleton">
        <Skeleton width="200px" height="24px" />
        <Skeleton width="100px" height="16px" style={{ marginTop: '8px' }} />
      </div>
      <SkeletonMobilePhotoGrid count={6} />
    </div>
  );
}

export function SkeletonSharesList({ count = 3 }) {
  return (
    <div className="shares-list skeleton-shares">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="share-item skeleton-share-item">
          <div className="share-item-header">
            <Skeleton width="150px" height="20px" />
            <Skeleton width="80px" height="20px" />
          </div>
          <div className="share-item-details">
            <SkeletonText lines={2} width="90%" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function SkeletonTagsList({ count = 5 }) {
  return (
    <div className="tags-list skeleton-tags">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="tag-item skeleton-tag">
          <Skeleton width="80px" height="28px" borderRadius="14px" />
        </div>
      ))}
    </div>
  );
}

export function SkeletonFullPage() {
  const isMobile = useIsMobile();
  
  if (isMobile) {
    return (
      <div className="skeleton-full-page mobile">
        <SkeletonMobileHeader />
        <SkeletonMobilePhotoGrid count={12} />
      </div>
    );
  }
  
  return (
    <div className="skeleton-full-page desktop">
      <div className="skeleton-nav">
        <Skeleton width="150px" height="40px" />
        <div style={{ display: 'flex', gap: '16px' }}>
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} width="80px" height="32px" />
          ))}
        </div>
      </div>
      <SkeletonPhotoGrid count={36} />
    </div>
  );
}

export function SkeletonShareCard() {
  return (
    <div className="share-card skeleton-share">
      <div className="share-card-header">
        <SkeletonText lines={1} width="200px" />
        <Skeleton width="80px" height="32px" />
      </div>
      <div className="share-card-content">
        <SkeletonText lines={2} />
      </div>
      <div className="share-card-footer">
        <Skeleton width="100px" height="20px" />
        <Skeleton width="100px" height="20px" />
      </div>
    </div>
  );
}

export function SkeletonAnalyticsCard() {
  return (
    <div className="analytics-card skeleton-analytics">
      <div className="analytics-icon">
        <Skeleton width="40px" height="40px" borderRadius="50%" />
      </div>
      <div className="analytics-content">
        <Skeleton width="80px" height="24px" style={{ marginBottom: '4px' }} />
        <Skeleton width="100px" height="14px" />
      </div>
    </div>
  );
}

export default Skeleton;
